// src/config/api.config.ts
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { refreshAccessToken, setAccessToken, clearAccessToken, getAccessToken, clearRefreshToken } from 'utils/auth/auth';
import { store } from 'config/redux/store';
import { logout } from 'config/redux/slices/userSlice';

interface JwtPayload {
  exp: number;
}

const api = axios.create({
  baseURL: process.env.REACT_APP_FAMOUS_CLICKER_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track if a refresh is in progress to prevent multiple simultaneous refreshes
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const subscribeTokenRefresh = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb);
};

const onRefreshed = (token: string) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

// Check if token is expired or about to expire (within 10 seconds)
const isTokenExpiredOrExpiring = (token: string): boolean => {
  try {
    const decoded: JwtPayload = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    return decoded.exp < currentTime + 10;
  } catch {
    return true;
  }
};

api.interceptors.request.use(
  async (config) => {
    let token = getAccessToken();

    if (token) {
      // Proactively refresh if token is expired or about to expire
      if (isTokenExpiredOrExpiring(token)) {
        if (!isRefreshing) {
          isRefreshing = true;
          try {
            const newToken = await refreshAccessToken(store.dispatch);
            if (newToken) {
              token = newToken;
              onRefreshed(newToken);
            }
          } catch {
            // Let the request proceed, 401 handler will catch it
          } finally {
            isRefreshing = false;
          }
        } else {
          // Wait for the ongoing refresh to complete
          token = await new Promise<string>((resolve) => {
            subscribeTokenRefresh((newToken: string) => {
              resolve(newToken);
            });
          });
        }
      }
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 errors - token might have expired between request and response
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const newAccessToken = await refreshAccessToken(store.dispatch);

          if (!newAccessToken) {
            throw new Error('Failed to refresh access token');
          }

          setAccessToken(newAccessToken);
          onRefreshed(newAccessToken);
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          store.dispatch(logout());
          clearAccessToken();
          clearRefreshToken();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      } else {
        // Wait for the ongoing refresh to complete
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((newToken: string) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(api(originalRequest));
          });
          setTimeout(() => {
            reject(new Error('Token refresh timeout'));
          }, 10000);
        });
      }
    }
    return Promise.reject(error);
  }
);

export default api;